import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SystemConfigurationSection = () => {
  const [config, setConfig] = useState({
    notifications: {
      emailEnabled: true,
      smsEnabled: false,
      slackEnabled: true,
      dailyDigest: true,
      instantAlerts: true
    },
    dataRetention: {
      violationHistory: '24',
      auditLogs: '12',
      exportedReports: '6',
      archivedEmployees: '36'
    },
    integrations: {
      hrmsEndpoint: 'https://api.company.com/hrms',
      payrollEndpoint: 'https://api.company.com/payroll',
      slackWebhook: 'https://hooks.slack.com/services/xxx',
      apiKey: '••••••••••••••••'
    },
    permissions: {
      managerCanEdit: true,
      managerCanDelete: false,
      hrCanOverride: true,
      employeeCanView: true
    }
  });
  const [activeSection, setActiveSection] = useState('notifications');
  const [showApiKey, setShowApiKey] = useState(false);

  const retentionOptions = [
    { value: '3', label: '3 months' },
    { value: '6', label: '6 months' },
    { value: '12', label: '12 months' },
    { value: '24', label: '24 months' },
    { value: '36', label: '36 months' },
    { value: 'indefinite', label: 'Indefinite' }
  ];

  const handleNotificationChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      notifications: { ...prev?.notifications, [key]: value }
    }));
  };

  const handleRetentionChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      dataRetention: { ...prev?.dataRetention, [key]: value }
    }));
  };

  const handleIntegrationChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      integrations: { ...prev?.integrations, [key]: value }
    }));
  };

  const handlePermissionChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      permissions: { ...prev?.permissions, [key]: value }
    }));
  };

  const sections = [
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'retention', label: 'Data Retention', icon: 'Database' },
    { id: 'integrations', label: 'Integrations', icon: 'Link' },
    { id: 'permissions', label: 'Permissions', icon: 'Lock' }
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="font-heading font-semibold text-xl text-foreground mb-1">
          System Configuration
        </h2>
        <p className="font-body text-sm text-muted-foreground">
          Global settings, notification preferences, integrations, and user permissions
        </p>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {sections?.map((section) => (
          <button
            key={section?.id}
            onClick={() => setActiveSection(section?.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap
              transition-smooth font-body font-medium
              ${
                activeSection === section?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }
            `}
          >
            <Icon name={section?.icon} size={18} />
            <span>{section?.label}</span>
          </button>
        ))}
      </div>

      {/* Notifications Section */}
      {activeSection === 'notifications' && (
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-heading font-semibold text-base text-foreground mb-4">
              Notification Channels
            </h3>
            <div className="space-y-3">
              <Checkbox
                label="Email Notifications"
                description="Send compliance alerts and reports via email"
                checked={config?.notifications?.emailEnabled}
                onChange={(e) => handleNotificationChange('emailEnabled', e?.target?.checked)}
              />
              <Checkbox
                label="SMS Notifications"
                description="Send critical alerts via SMS text messages"
                checked={config?.notifications?.smsEnabled}
                onChange={(e) => handleNotificationChange('smsEnabled', e?.target?.checked)}
              />
              <Checkbox
                label="Slack Integration"
                description="Post compliance updates to Slack channels"
                checked={config?.notifications?.slackEnabled}
                onChange={(e) => handleNotificationChange('slackEnabled', e?.target?.checked)}
              />
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-heading font-semibold text-base text-foreground mb-4">
              Notification Preferences
            </h3>
            <div className="space-y-3">
              <Checkbox
                label="Daily Digest"
                description="Receive daily summary of compliance activities"
                checked={config?.notifications?.dailyDigest}
                onChange={(e) => handleNotificationChange('dailyDigest', e?.target?.checked)}
              />
              <Checkbox
                label="Instant Alerts"
                description="Receive immediate notifications for critical violations"
                checked={config?.notifications?.instantAlerts}
                onChange={(e) => handleNotificationChange('instantAlerts', e?.target?.checked)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Data Retention Section */}
      {activeSection === 'retention' && (
        <div className="space-y-4">
          <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
              <div>
                <h4 className="font-heading font-semibold text-sm text-foreground mb-1">
                  Data Retention Policy
                </h4>
                <p className="text-sm text-muted-foreground">
                  Configure how long different types of data are retained in the system. Expired data will be automatically archived or deleted based on your policy.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="space-y-4">
              <Select
                label="Violation History"
                description="How long to keep employee violation records"
                options={retentionOptions}
                value={config?.dataRetention?.violationHistory}
                onChange={(value) => handleRetentionChange('violationHistory', value)}
              />
              <Select
                label="Audit Logs"
                description="How long to keep system audit and change logs"
                options={retentionOptions}
                value={config?.dataRetention?.auditLogs}
                onChange={(value) => handleRetentionChange('auditLogs', value)}
              />
              <Select
                label="Exported Reports"
                description="How long to keep generated report files"
                options={retentionOptions}
                value={config?.dataRetention?.exportedReports}
                onChange={(value) => handleRetentionChange('exportedReports', value)}
              />
              <Select
                label="Archived Employees"
                description="How long to keep data for terminated employees"
                options={retentionOptions}
                value={config?.dataRetention?.archivedEmployees}
                onChange={(value) => handleRetentionChange('archivedEmployees', value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Integrations Section */}
      {activeSection === 'integrations' && (
        <div className="space-y-4">
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary mt-0.5" />
              <div>
                <h4 className="font-heading font-semibold text-sm text-foreground mb-1">
                  External System Integrations
                </h4>
                <p className="text-sm text-muted-foreground">
                  Connect ComplianceTracker with your existing HR systems, payroll platforms, and communication tools.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="space-y-4">
              <Input
                label="HRMS API Endpoint"
                description="REST API endpoint for your HR Management System"
                value={config?.integrations?.hrmsEndpoint}
                onChange={(e) => handleIntegrationChange('hrmsEndpoint', e?.target?.value)}
                placeholder="https://api.company.com/hrms"
              />
              <Input
                label="Payroll API Endpoint"
                description="REST API endpoint for payroll system integration"
                value={config?.integrations?.payrollEndpoint}
                onChange={(e) => handleIntegrationChange('payrollEndpoint', e?.target?.value)}
                placeholder="https://api.company.com/payroll"
              />
              <Input
                label="Slack Webhook URL"
                description="Incoming webhook URL for Slack notifications"
                value={config?.integrations?.slackWebhook}
                onChange={(e) => handleIntegrationChange('slackWebhook', e?.target?.value)}
                placeholder="https://hooks.slack.com/services/xxx"
              />
              <div>
                <Input
                  label="API Authentication Key"
                  description="Secure API key for external integrations"
                  type={showApiKey ? 'text' : 'password'}
                  value={config?.integrations?.apiKey}
                  onChange={(e) => handleIntegrationChange('apiKey', e?.target?.value)}
                  placeholder="Enter API key"
                />
                <button
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="text-sm text-primary hover:underline mt-1"
                >
                  {showApiKey ? 'Hide' : 'Show'} API Key
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" iconName="RefreshCw" iconPosition="left">
              Test Connection
            </Button>
            <Button variant="default" iconName="Save" iconPosition="left">
              Save Integration Settings
            </Button>
          </div>
        </div>
      )}

      {/* Permissions Section */}
      {activeSection === 'permissions' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-heading font-semibold text-base text-foreground mb-4">
              Manager Permissions
            </h3>
            <div className="space-y-3">
              <Checkbox
                label="Can Edit Violations"
                description="Allow managers to edit violation details and point values"
                checked={config?.permissions?.managerCanEdit}
                onChange={(e) => handlePermissionChange('managerCanEdit', e?.target?.checked)}
              />
              <Checkbox
                label="Can Delete Violations"
                description="Allow managers to permanently delete violation records"
                checked={config?.permissions?.managerCanDelete}
                onChange={(e) => handlePermissionChange('managerCanDelete', e?.target?.checked)}
              />
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-heading font-semibold text-base text-foreground mb-4">
              HR Permissions
            </h3>
            <div className="space-y-3">
              <Checkbox
                label="Can Override Point Calculations"
                description="Allow HR to manually adjust point totals and thresholds"
                checked={config?.permissions?.hrCanOverride}
                onChange={(e) => handlePermissionChange('hrCanOverride', e?.target?.checked)}
              />
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-heading font-semibold text-base text-foreground mb-4">
              Employee Permissions
            </h3>
            <div className="space-y-3">
              <Checkbox
                label="Can View Own Violations"
                description="Allow employees to view their own compliance records"
                checked={config?.permissions?.employeeCanView}
                onChange={(e) => handlePermissionChange('employeeCanView', e?.target?.checked)}
              />
            </div>
          </div>

          <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="public/assets/images/Real_logo-1771587686730.png" size={20} className="text-warning mt-0.5" />
              <div>
                <h4 className="font-heading font-semibold text-sm text-foreground mb-1">
                  Permission Changes Take Effect Immediately
                </h4>
                <p className="text-sm text-muted-foreground">
                  Any changes to user permissions will be applied system-wide immediately. Ensure you review changes carefully before saving.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-border">
        <Button variant="outline" iconName="RotateCcw" iconPosition="left">
          Reset to Defaults
        </Button>
        <Button variant="default" iconName="Save" iconPosition="left">
          Save Configuration
        </Button>
      </div>
    </div>
  );
};

export default SystemConfigurationSection;